// src/hooks/useFileUpload.js
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { getExtensionFromMimeType } from '../utils/mimeTypeHandler';
import { authedApiFetch } from '../utils/Api';

const allowedExtensions = ['pdf', 'xlsx', 'csv', 'tsv', 'pptx', 'docx'];

const useFileUpload = (tenantId, dealId, onSourceAdded) => {
	// State for modal and notifications
	const [openModal, setOpenModal] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState(null);

	// Handle opening and closing the upload modal
	const handleOpenModal = () => {
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		setOpenModal(false);
	};

	// Clear notifications
	const handleCloseSnackbar = () => {
		setError(null);
		setSuccess(false);
	};

	// File upload handler
	const onDrop = useCallback(
		async (acceptedFiles, rejectedFiles) => {
			if (rejectedFiles && rejectedFiles.length > 0) {
				const errorMessages = rejectedFiles
					.map((r) => r.errors.map((e) => e.message).join(', '))
					.join('; ');
				setError(errorMessages);
				return;
			}

			let processedFilesCount = 0;
			const newSources = [];

			acceptedFiles.forEach(async (file) => {
				const mimeType = file.type;
				const fileExtension = getExtensionFromMimeType(mimeType);

				if (!allowedExtensions.includes(fileExtension)) {
					setError(
						`Unsupported file type: ${mimeType}. Allowed types are ${allowedExtensions.join(', ')}.`
					);
					return;
				}

				const reader = new FileReader();
				reader.onload = async (e) => {
					const fileContents = e.target.result;
					const base64File = btoa(
						new Uint8Array(fileContents).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							''
						)
					);

					const newSource = {
						id: Date.now() + processedFilesCount, // Temporary ID
						name: file.name,
						type: 'document',
						format: fileExtension,
						url: '#', // Placeholder URL
					};

					try {
						const response = await authedApiFetch({
							endpoint: `/tenants/${tenantId}/deals/${dealId}/source_monitors`,
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							payload: {
								file_name: newSource.name,
								file_contents: base64File,
								source_type: 'document',
							},
						});

						newSource.id = response.id;
						newSources.push(newSource);
						processedFilesCount++;

						if (processedFilesCount === acceptedFiles.length) {
							// Call the callback with new sources
							onSourceAdded(newSources);
							setSuccess(true);
							handleCloseModal();
						}
					} catch (error) {
						setError(`Error uploading file: ${error.message}`);
					}
				};

				reader.readAsArrayBuffer(file);
			});
		},
		[tenantId, dealId, onSourceAdded]
	);

	// Dropzone configuration
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		maxSize: 2 * 1024 * 1024 * 1024, // 2GB limit
		onDropRejected: (rejectedFiles) => {
			const rejection = rejectedFiles[0];
			if (rejection.errors) {
				if (rejection.errors.some((e) => e.code === 'file-too-large')) {
					setError('File too large. Maximum size is 2GB.');
				} else {
					setError(
						'File upload failed: ' +
							rejection.errors.map((e) => e.message).join(', ')
					);
				}
			}
		},
	});

	return {
		openModal,
		success,
		error,
		isDragActive,
		handleOpenModal,
		handleCloseModal,
		handleCloseSnackbar,
		getRootProps,
		getInputProps,
		allowedExtensions,
	};
};

export default useFileUpload;
